import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Add, Delete, Edit, Group, Visibility } from "@mui/icons-material";
import { apiService } from "../../api/services/apiService";
import { useAuth } from "../../contexts/AuthContext";
import type { ProductManagementGroup, TenantMember } from "../../types";
import { isProductManager, isTenantAdmin } from "../../utils/appRoles";
import PmgDetailDialog from "./PmgDetailDialog";

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

const ProductManagementGroupsTab: React.FC = () => {
  const { user } = useAuth();
  const userEmail = user?.email?.toLowerCase() ?? "";
  const canCreate = isProductManager(user?.roles) || isTenantAdmin(user?.roles);

  const [groups, setGroups] = useState<ProductManagementGroup[]>([]);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createSaving, setCreateSaving] = useState(false);

  const [detailGroup, setDetailGroup] = useState<ProductManagementGroup | null>(null);

  const memberEmails = useMemo(() => {
    const fromTenant = members.map((m) => m.email).filter(Boolean);
    const fromGroups = groups.flatMap((g) => [...g.owners, ...g.members]);
    return [...new Set([...fromTenant, ...fromGroups])].sort();
  }, [groups, members]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupList, tenantMembers] = await Promise.all([
        apiService.getProductManagementGroups(),
        isTenantAdmin(user?.roles)
          ? apiService.getTenantMembers().catch(() => [] as TenantMember[])
          : Promise.resolve([] as TenantMember[]),
      ]);
      setGroups(groupList);
      setMembers(tenantMembers);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, [user?.roles]);

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  const canManageGroup = (group: ProductManagementGroup) =>
    isTenantAdmin(user?.roles) ||
    group.owners.some((o) => o.toLowerCase() === userEmail);

  const canViewGroup = (group: ProductManagementGroup) =>
    canManageGroup(group) ||
    group.members.some((m) => m.toLowerCase() === userEmail);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setCreateSaving(true);
    setError(null);
    try {
      await apiService.createProductManagementGroup({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
      });
      setCreateOpen(false);
      setCreateName("");
      setCreateDescription("");
      setSuccess("Group created");
      await loadGroups();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create group");
    } finally {
      setCreateSaving(false);
    }
  };

  const openDetail = async (uuid: string) => {
    try {
      const group = await apiService.getProductManagementGroup(uuid);
      setDetailGroup(group);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load group");
    }
  };

  const refreshDetail = async (uuid: string) => {
    const group = await apiService.getProductManagementGroup(uuid);
    setDetailGroup(group);
    await loadGroups();
  };

  const handleDelete = async (group: ProductManagementGroup) => {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    try {
      await apiService.deleteProductManagementGroup(group.uuid);
      setSuccess("Group deleted");
      await loadGroups();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete group");
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Groups control who can modify assigned catalog assets. Unassigned assets remain editable by all
          product managers.
        </Typography>
        {canCreate && (
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>
            Create Group
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={tablePaperSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Owners</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Assets</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No product management groups yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group.uuid} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Group fontSize="small" color="action" />
                        {group.name}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{group.owners.length}</TableCell>
                    <TableCell align="right">{group.members.length}</TableCell>
                    <TableCell align="right">{group.assignedAssetCount}</TableCell>
                    <TableCell align="right">
                      {canManageGroup(group) ? (
                        <Tooltip title="Manage group">
                          <IconButton
                            size="small"
                            onClick={() => void openDetail(group.uuid)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : canViewGroup(group) ? (
                        <Tooltip title="View group">
                          <IconButton
                            size="small"
                            onClick={() => void openDetail(group.uuid)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete group">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void handleDelete(group)}
                          disabled={!canManageGroup(group)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Product Management Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            required
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={() => void handleCreate()} variant="contained" disabled={createSaving}>
            {createSaving ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <PmgDetailDialog
        group={detailGroup}
        open={detailGroup !== null}
        canManage={detailGroup ? canManageGroup(detailGroup) : false}
        memberEmails={memberEmails}
        onClose={() => setDetailGroup(null)}
        onRefresh={refreshDetail}
        onError={(message) => setError(message)}
      />
    </Box>
  );
};

export default ProductManagementGroupsTab;
